import clsx from 'clsx';
import Image from 'next/image';
import type { FC, ReactNode } from 'react';

import Title from '@/c/common/Title';
import NavBar from '@/c/common/NavBar';
import Footer from '@/c/common/Footer';
import GenericDividingTitle from '@/c/generic/GenericDividingTitle';

import heroBgImage from '@/assets/about-hero-bg.jpg';
import partner1Pic from '@/assets/members/partner-1.jpg';
import partner2Pic from '@/assets/members/partner-2.jpg';
import partner3Pic from '@/assets/members/partner-3.jpg';
import partner4Pic from '@/assets/members/partner-4.jpg';
import member1Pic from '@/assets/members/member-1.jpg';
import member2Pic from '@/assets/members/member-2.jpg';
import member3Pic from '@/assets/members/member-3.jpg';
import member4Pic from '@/assets/members/member-4.jpg';
import member5Pic from '@/assets/members/member-5.jpg';
import member6Pic from '@/assets/members/member-6.jpg';
import member7Pic from '@/assets/members/member-7.jpg';
import member8Pic from '@/assets/members/member-8.jpg';
import member9Pic from '@/assets/members/member-9.jpg';

const HeroDescriptions = () => (
  <div className="">
    {/* Mobile */}
    <div className="block sm:hidden">
      <div className="relative">
        {/* Background image */}
        <Image
          priority
          alt="About Oliver Interior Design hero image"
          src={heroBgImage}
          layout="fill"
          placeholder="blur"
          objectFit="cover"
          className="opacity-50"
        />
        <div className="relative w-full px-4 py-6 space-y-6 max-w-3xl">
          {/* Title */}
          <div className="tracking-wider leading-sung space-y-2">
            <p className="text-3xl font-thin">從業超過 40 年</p>
            <p className="text-xl font-light">
              提供最有溫度、
              <br />
              情感及闡釋多元視角的好設計
            </p>
          </div>
          {/* Paragraphs */}
          <div className="space-y-6 leading-normal font-light">
            <div className="space-y-2">
              <p>1979 年創辦人鍾雍光以成功大學為名，成立「成大設計事務所」，不忘母校悉心栽培及保留初心；</p>
              <p>2009 年鍾鼎設計總監傳承父業，正式更名為「奧立佛室內設計 Oliver Interior Design」，以創辦人英文名字 Oliver 為名。</p>
            </div>
            <div className="space-y-2">
              <p>
                除了感懷父親精神永存，鍾鼎設計總監更致力於『人，即為空間主角』思維，將 Oliver 拉丁語中橄欖樹意象：
                大自然溫煦、博愛和平和因地而生納入設計，依循人本觀念將室內設計擴大格局。
              </p>
            </div>
            <div className="space-y-2">
              <p>
                2020年，奧立佛室內設計欲將設計版圖提升，與竺居設計共創聯合設計，
                三人團隊藉多年不同領域陶冶相互激盪，讓室內設計回歸本質，不只是美感體現，而是關乎人與人之間心境寫照；
              </p>
              <p>從業超過40 年，不變的是提供最有溫度、情感及闡釋多元視角的好設計。</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    {/* Desktops */}
    <div className="hidden sm:block py-8">
      <div className="relative">
        {/* Background image */}
        <div className="flex justify-end">
          <div className="w-[90%] md:w-[60%] max-w-2xl">
            <Image
              priority
              alt="About Oliver Interior Design hero image"
              src={heroBgImage}
              layout="responsive"
              placeholder="blur"
            />
          </div>
        </div>
        <div className="absolute top-[3.88%] lg:top-[12.888%] xl:top-[14.888%] left-0 w-full px-4 py-6 space-y-8 max-w-3xl">
          {/* Title */}
          <div className="text-3xl font-light tracking-wider leading-normal">
            <p>從業超過 40 年</p>
            <p>提供最有溫度、情感及闡釋多元視角的好設計</p>
          </div>
          {/* Paragraphs */}
          <div className="space-y-8 leading-snug font-light">
            <div className="space-y-2">
              <p>1979 年創辦人鍾雍光以成功大學為名，成立「成大設計事務所」，不忘母校悉心栽培及保留初心；</p>
              <p>2009 年鍾鼎設計總監傳承父業，正式更名為「奧立佛室內設計 Oliver Interior Design」，以創辦人英文名字 Oliver 為名。</p>
            </div>
            <div className="space-y-2">
              <p>
                除了感懷父親精神永存，鍾鼎設計總監更致力於『人，即為空間主角』思維，將 Oliver 拉丁語中橄欖樹意象：
                大自然溫煦、博愛和平和因地而生納入設計，依循人本觀念將室內設計擴大格局。
              </p>
            </div>
            <div className="space-y-2">
              <p>
                2020年，奧立佛室內設計欲將設計版圖提升，與竺居設計共創聯合設計，
                三人團隊藉多年不同領域陶冶相互激盪，讓室內設計回歸本質，不只是美感體現，而是關乎人與人之間心境寫照；
              </p>
              <p>從業超過40 年，不變的是提供最有溫度、情感及闡釋多元視角的好設計。</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

interface PartnerItemProps {
  picture: StaticImageData,
  title: string,
  name: string,
  education: string,
  topPadding?: boolean,
}
const PartnerItem: FC<PartnerItemProps> = ({
  picture, title, name, education, topPadding = false,
}) => (
  <div className={clsx(!!topPadding && 'md:pt-[20%]')}>
    <div className="border border-gray-200">
      {/* Picture */}
      <div className="w-full flex">
        <Image
          src={picture}
          alt={`${title} ${name}`}
          placeholder="blur"
        />
      </div>
      <div className="p-4 space-y-1.5 sm:space-y-3">
        {/* Title */}
        <div className="relative flex justify-center">
          <div className="absolute w-full top-[50%] translate-y-[-50%]">
            <div className="bg-black h-[1px]" />
          </div>
          <div className="relative inline bg-white px-2 lg:px-3 sm:text-lg !leading-none font-light">{title}</div>
        </div>
        {/* Name */}
        <div className="text-2xl sm:text-3xl tracking-wider text-center">{name}</div>
        {/* Education */}
        <div className="text-gray-600 text-center text-sm sm:text-base font-light">{education}</div>
      </div>
    </div>
  </div>
);

const PartnersGrid = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 lg:gap-4 px-4 lg:px-8">
    <PartnerItem
      picture={partner1Pic}
      name="鍾鼎"
      title="設計總監"
      education="中正大學企管系"
    />
    <PartnerItem
      topPadding
      picture={partner2Pic}
      name="鍾黎"
      title="設計總監"
      education="淡江大學建築學系"
    />
    <PartnerItem
      picture={partner3Pic}
      name="謝雨竹"
      title="營運總監"
      education="實踐大學建築研究所"
    />
    <PartnerItem
      topPadding
      picture={partner4Pic}
      name="周翧侑"
      title="品牌財務經理"
      education="國立台北商業大學"
    />
  </div>
);

interface TeamMemberItemProps {
  picture: StaticImageData,
  name: string,
}
const TeamMemberItem: FC<TeamMemberItemProps> = ({ picture, name }) => (
  <div className="border border-gray-200">
    {/* Picture */}
    <div className="w-full flex">
      <Image
        src={picture}
        alt={`設計師 ${name}`}
        placeholder="blur"
      />
    </div>
    <div className="px-4 py-3">
      {/* Name */}
      <div className="text-xl tracking-wider">{name}</div>
      {/* Title */}
      <div className="text-gray-400 text-sm font-light">Designer</div>
    </div>
  </div>
);

const TeamMembersGrid = () => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 lg:gap-4 px-4 lg:px-8">
    <TeamMemberItem
      picture={member1Pic}
      name="歐金源"
    />
    <TeamMemberItem
      picture={member2Pic}
      name="李佳柔"
    />
    <TeamMemberItem
      picture={member3Pic}
      name="陳家緯"
    />
    <TeamMemberItem
      picture={member4Pic}
      name="高可潔"
    />
    <TeamMemberItem
      picture={member5Pic}
      name="陳蔓竹"
    />
    <TeamMemberItem
      picture={member6Pic}
      name="黃俊翰"
    />
    <TeamMemberItem
      picture={member7Pic}
      name="林佳萱"
    />
    <TeamMemberItem
      picture={member8Pic}
      name="王奕雯"
    />
    <TeamMemberItem
      picture={member9Pic}
      name="莊子承"
    />
  </div>
);

const TeamMemberSection = () => (
  <div className="space-y-4 md:space-y-12">
    <GenericDividingTitle>團隊成員</GenericDividingTitle>
    <PartnersGrid />
    <TeamMembersGrid />
  </div>
);

interface PhilosophyBlockProps {
  leftSide: ReactNode,
  rightSide: ReactNode,
}
const PhilosophyBlock: FC<PhilosophyBlockProps> = ({ leftSide, rightSide }) => (
  <div className="sm:grid sm:grid-cols-12 mb-4 sm:mb-12">
    {/* Title */}
    <div className="sm:col-span-3 sm:border-r border-black text-3xl md:text-xl lg:text-2xl font-thin pr-10 mb-6 sm:mb-0">{leftSide}</div>
    {/* Content */}
    <div className="sm:col-span-9 sm:pl-10 pb-4 space-y-4 tracking-wide leading-relaxed font-light">{rightSide}</div>
  </div>
);

const PhilosophySection = () => (
  <div className="space-y-4 md:space-y-12">
    <GenericDividingTitle>設計理念</GenericDividingTitle>
    <div className="px-4 sm:px-0 sm:grid grid-cols-1 grid-rows-3 max-w-screen-lg mx-auto">
      <PhilosophyBlock
        leftSide="Environmentally Responsible Design"
        rightSide={(
          <>
            <p className="hidden sm:block">
              隨著地球暖化、氣候變遷等人類所製造的強勢破壞，
              <br />
              建築、室內設計當然不可避免有其影響性。
            </p>
            <p className="hidden sm:block">
              「設計」和「環境」如何在自然情況下和平共存？
              <br />
              既然無法避免耗損，我們認為基本義務是盡可能將影響降至最低，
              <br />
              堅持提供健康、無毒及安全的居住環境給屋主。
            </p>
            <p className="hidden sm:block">
              奧立佛室內設計 Oliver Interior Design
              <br />
              專注於比例、材料和空間平衡，用心與使用者溝通並了解其日常習慣，
              <br />
              在層層剖析後重新組合，創造出真正符合使用需求、簡而不減的居家空間。
            </p>
            <p className="block sm:hidden">
              隨著地球暖化、氣候變遷等人類所製造的強勢破壞，
              建築、室內設計當然不可避免有其影響性。
            </p>
            <p className="block sm:hidden">
              「設計」和「環境」如何在自然情況下和平共存？
              <br />
              既然無法避免耗損，我們認為基本義務是盡可能將影響降至最低，
              堅持提供健康、無毒及安全的居住環境給屋主。
            </p>
            <p className="block sm:hidden">
              奧立佛室內設計 Oliver Interior Design
              <br />
              專注於比例、材料和空間平衡，用心與使用者溝通並了解其日常習慣，
              在層層剖析後重新組合，創造出真正符合使用需求、簡而不減的居家空間。
            </p>
          </>
        )}
      />
      <PhilosophyBlock
        leftSide="a design for life"
        rightSide={(
          <>
            <p className="hidden sm:block">
              透過本質上保留簡潔性，創造出空間、時間與光感脈絡，好宅不需要過多綴飾，
              <br />
              有了好採光、通風及良好動線鋪排，搭配家具跟恰當軟件，再加上屋主，就能圓滿整個居所。
            </p>
            <p className="hidden sm:block">
              如同生活沒有既定公式，室內設計也無框架規則；
              <br />
              拋棄浮華裝飾，我們著重於人與人、人與環境連結產生的相互關係，
              <br />
              期望不管三個月、十年，居住者在每個生命階段，對於空間都能產生不同情感與定義。
            </p>
            <p className="block sm:hidden">
              透過本質上保留簡潔性，創造出空間、時間與光感脈絡，好宅不需要過多綴飾，
              有了好採光、通風及良好動線鋪排，搭配家具跟恰當軟件，再加上屋主，就能圓滿整個居所。
            </p>
            <p className="block sm:hidden">
              如同生活沒有既定公式，室內設計也無框架規則；
              <br />
              拋棄浮華裝飾，我們著重於人與人、人與環境連結產生的相互關係，
              期望不管三個月、十年，居住者在每個生命階段，對於空間都能產生不同情感與定義。
            </p>
          </>
        )}
      />
      <PhilosophyBlock
        leftSide="Revieworks"
        rightSide={(
          <>
            <p className="hidden sm:block">
              我們重新審視每件事物，以獨特觀看角度，了解每件事物價值；
              <br />
              並隨時注意其中細微變化，追求完美意象。
            </p>
            <p className="hidden sm:block">
              奧立佛室內設計 x 竺居設計聯合工作團隊，悉心看待每一層結構間的動靜環節，
              <br />
              將永續自然、人與空間列為設計宗旨。
            </p>
            <p className="block sm:hidden">
              我們重新審視每件事物，以獨特觀看角度，了解每件事物價值；
              <br />
              並隨時注意其中細微變化，追求完美意象。
            </p>
            <p className="block sm:hidden">
              奧立佛室內設計 x 竺居設計聯合工作團隊，悉心看待每一層結構間的動靜環節，
              將永續自然、人與空間列為設計宗旨。
            </p>
          </>
        )}
      />
    </div>
  </div>
);

const Page = () => (
  <>
    <Title>公司介紹</Title>
    <NavBar />
    <div className="sm:container mx-auto mb-20 space-y-10 sm:space-y-12 lg:space-y-20">
      <HeroDescriptions />
      <TeamMemberSection />
      <PhilosophySection />
    </div>
    <Footer />
  </>
);

export default Page;
